import { useStore, type State } from "./store";

// The last view of every chat is kept on this computer, so the Hub opens instantly
// and only fetches what changed. Cleared on sign-out.

// Bump KEY whenever a cached shape changes, so old copies are ignored instead of breaking the screen.
const DB = "hub", STORE = "kv", KEY = "snap-v5";
const KEEP = 80;

function open(): Promise<IDBDatabase> {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((res, rej) => {
    const t = db.transaction(STORE, mode);
    const r = fn(t.objectStore(STORE));
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}

type Snap = Pick<State, "me" | "users" | "rooms" | "states" | "tasks" | "msgs" | "neoUrl" | "serverTime" | "pending">;

export async function readCache(): Promise<Snap | null> {
  try { return (await tx("readonly", (s) => s.get(KEY))) ?? null; } catch { return null; }
}
export async function clearCache() {
  try { await tx("readwrite", (s) => s.clear()); } catch {}
}

let timer: number | undefined;
export function keepCached() {
  useStore.subscribe((s, prev) => {
    if (s.phase !== "ready" || (s.msgs === prev.msgs && s.tasks === prev.tasks && s.states === prev.states && s.rooms === prev.rooms && s.pending === prev.pending)) return;
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      const s = useStore.getState();
      const msgs = Object.fromEntries(Object.entries(s.msgs).map(([k, v]) => [k, v.slice(-KEEP)]));
      const snap: Snap = { me: s.me, users: s.users, rooms: s.rooms, states: s.states, tasks: s.tasks, msgs, neoUrl: s.neoUrl, serverTime: s.serverTime, pending: s.pending };
      tx("readwrite", (st) => st.put(snap, KEY)).catch(() => {});
    }, 800);
  });
}
